/**
 * AppHeader — Top navigation bar with breadcrumbs
 *
 * Brand + Breadcrumb trail + Theme toggle + User menu
 * Uses --header-background/foreground CSS variables for theming.
 * Pages register entity names via BreadcrumbContext for UUID resolution.
 */

import { Menu, Sun, Moon, Monitor, ChevronRight } from 'lucide-react';
import { useLocation, Link } from 'react-router-dom';
import { useTheme } from '../ThemeProvider';
import { UserMenu } from './UserMenu';
import { NotificationBell } from './NotificationBell';
import { OutstandingBalanceButton } from './OutstandingBalanceButton';
import { EntryCloseHeaderIndicator } from './EntryCloseHeaderIndicator';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { useIsAuthenticated } from '../../state_management_and_stores/auth.store';

interface AppHeaderProps {
  onMobileMenuToggle: () => void;
}

// Map route segments to display names
const segmentLabels: Record<string, string> = {
  dashboard: 'Dashboard',
  jackpots: 'Jackpots',
  events: 'Events',
  standings: 'Standings',
  profile: 'Profile',
  support: 'Support',
  settings: 'Settings',
  users: 'Users',
  organizations: 'Organizations',
  admin: 'Admin',
  delegate: 'Delegate',
  rodeos: 'Rodeos',
  'live-scoring': 'Live Scoring',
  'prize-management': 'Prize Management',
  draws: 'Manage Draws',
  login: 'Login',
  register: 'Register',
  my: 'My',
  'buddy-groups': 'Buddy Groups',
};

function isUuid(s: string) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s) ||
    /^[a-f0-9]{20,}$/i.test(s);
}

export function AppHeader({ onMobileMenuToggle }: AppHeaderProps) {
  const { mode, setMode, actualMode } = useTheme();
  const location = useLocation();
  const { overrides, trail } = useBreadcrumb();
  const isAuthenticated = useIsAuthenticated();

  const cycleTheme = () => {
    const next = mode === 'light' ? 'dark' : mode === 'dark' ? 'system' : 'light';
    setMode(next);
  };

  const ThemeIcon = mode === 'system' ? Monitor : actualMode === 'dark' ? Moon : Sun;

  // Build breadcrumb segments
  // If the page set a custom trail, use it directly. Otherwise derive from URL.
  let crumbs: { label: string; path: string }[];
  if (trail && trail.length > 0) {
    crumbs = trail;
  } else {
    const segments = location.pathname.split('/').filter(Boolean);
    crumbs = [];
    let accPath = '';
    for (const seg of segments) {
      accPath += `/${seg}`;
      if (isUuid(seg)) {
        const override = overrides.get(seg);
        if (override) {
          crumbs.push({ label: override.label, path: override.path || accPath });
        }
        continue;
      }
      const label = segmentLabels[seg] || seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      crumbs.push({ label, path: accPath });
    }
  }

  return (
    <header
      className="sticky top-0 z-40 flex h-14 items-center gap-4 border-b border-border px-4 md:px-6"
      style={{
        backgroundColor: 'hsl(var(--header-background))',
        color: 'hsl(var(--header-foreground))',
      }}
    >
      {/* Mobile hamburger */}
      <button
        onClick={onMobileMenuToggle}
        className="lg:hidden inline-flex items-center justify-center rounded-md p-2 opacity-80 hover:opacity-100 transition-opacity"
        aria-label="Toggle menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Brand + Breadcrumbs */}
      <div className="flex items-center gap-2 min-w-0">
        {/* Brand (icon + text) — both clickable, routes to dashboard for
            authed users (their home) or "/" for the rare unauth-in-AppHeader
            edge case. */}
        <Link
          to={isAuthenticated ? '/dashboard' : '/'}
          className="flex items-center gap-2 shrink-0 hover:opacity-80 transition-opacity"
        >
          <img
            src="/icons/HighCallRodeo-icon-white.png"
            alt=""
            className="h-[25px] w-auto shrink-0"
          />
          <span className="text-sm font-semibold tracking-[0.15em] uppercase shrink-0">
            HighCall Rodeo
          </span>
        </Link>

        {/* Breadcrumb trail */}
        {crumbs.length > 0 && crumbs[0].label !== 'Dashboard' && (
          <nav className="hidden sm:flex items-center gap-1 min-w-0 text-sm" aria-label="Breadcrumb">
            {crumbs.map((crumb, i) => (
              <span key={crumb.path} className="flex items-center gap-1 min-w-0">
                <ChevronRight className="h-3.5 w-3.5 opacity-40 shrink-0" />
                {i === crumbs.length - 1 ? (
                  <span className="font-medium truncate max-w-[200px]">{crumb.label}</span>
                ) : (
                  <Link
                    to={crumb.path}
                    className="opacity-60 hover:opacity-100 transition-opacity truncate max-w-[200px]"
                  >
                    {crumb.label}
                  </Link>
                )}
              </span>
            ))}
          </nav>
        )}
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Right side actions */}
      <div className="flex items-center gap-2">
        {/* Global "entries closing soon" indicator — surfaces the
            soonest-closing rodeo with a live countdown on every
            authenticated page so users can act before the close
            time even if they aren't on the dashboard. Self-hides
            when nothing's closing soon. */}
        {isAuthenticated && <EntryCloseHeaderIndicator />}

        {/* Outstanding balance CTA — only mounted when signed in so the
            underlying query doesn't fire on the public/login routes. */}
        {isAuthenticated && <OutstandingBalanceButton />}

        {/* Notification bell */}
        <NotificationBell />

        {/* Theme toggle */}
        <button
          onClick={cycleTheme}
          className="inline-flex items-center justify-center rounded-md p-2 opacity-60 hover:opacity-100 transition-opacity"
          aria-label={`Theme: ${mode}`}
          title={mode === 'system' ? `System (${actualMode})` : mode}
        >
          <ThemeIcon className="h-4 w-4" />
        </button>

        {/* User menu */}
        <UserMenu />
      </div>
    </header>
  );
}
